import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaTable, FaChartLine, FaVideo } from 'react-icons/fa';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TextField, Button, ButtonGroup, CircularProgress } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import axios from 'axios';

import { APP_BASE_URL, GET_TELEMETRY_HISTORY } from '../../../Common/EndPoints';
import CCTVHistoryGraph from './CCTVHistoryGraph';
import '../cctv-dashboard.scss';

type ViewMode = 'table' | 'graph';

interface CCTVHistoryRecord {
  id: string;
  timestamp: string;
  status: 'active' | 'inactive';
  health: string;
  message: string;
}

interface CCTVHistoryResponse {
  content: any[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
}

// Date range presets
const getDateRangePresets = () => [
  { label: 'Last 24 Hours', days: 1 },
  { label: 'Last 7 Days', days: 7 },
  { label: 'Last 30 Days', days: 30 },
  { label: 'Last 90 Days', days: 90 },
];

// Calculate date range from preset
const calculateDateRange = (days: number): { startTs: number; endTs: number } => {
  const endTs = Date.now();
  const startTs = endTs - (days * 24 * 60 * 60 * 1000);
  return { startTs, endTs };
};

// Fetch CCTV history
const fetchCCTVHistory = async (params: {
  deviceId: string;
  startTs?: number;
  endTs?: number;
  page?: number;
  pageSize?: number;
}): Promise<CCTVHistoryResponse> => {
  try {
    const response = await axios.get(`${APP_BASE_URL}${GET_TELEMETRY_HISTORY}`, {
      params: {
        deviceId: params.deviceId,
        startTs: params.startTs,
        endTs: params.endTs,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
        isModelView: true,
      },
    });

    const data = response.data?.data;
    return {
      content: data?.content || [],
      totalElements: data?.totalElements || 0,
      totalPages: data?.totalPages || 0,
      currentPage: data?.number || 0,
    };
  } catch (error) {
    console.error('Error fetching CCTV history:', error);
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      currentPage: 0,
    };
  }
};

// Transform raw history data
const transformHistoryData = (rawContent: any[]): CCTVHistoryRecord[] => {
  return rawContent.map((item, index) => {
    const dataItem = item?.data?.[0] || {};
    const values = dataItem?.values || {};
    const dateTime = dataItem?.dateTime || item?.ts || Date.now();

    // Determine status from values
    const pfcIn = (values['PFC_In'] || '').toString().toUpperCase();
    const health = (values['asset.health'] || '').toString().toUpperCase();
    const isActive = pfcIn === 'ACTIVE' || health === 'HEALTHY';

    return {
      id: `history-${index}-${dateTime}`,
      timestamp: new Date(dateTime).toISOString(),
      status: isActive ? 'active' : 'inactive',
      health: values['asset.health'] || values['PFC_In'] || 'Unknown',
      message: values['responseMeta']?.message || '-',
    };
  });
};

const CCTVCameraHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // State
  const [startTs, setStartTs] = useState<Dayjs | null>(dayjs().subtract(7, 'day'));
  const [endTs, setEndTs] = useState<Dayjs | null>(dayjs());
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [historyData, setHistoryData] = useState<CCTVHistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [graphPageSize] = useState(500); // Larger page size for graph view
  const [deviceName, setDeviceName] = useState('');

  // Date range presets
  const datePresets = getDateRangePresets();

  // Fetch history data
  const fetchHistory = useCallback(async (forGraph: boolean = false) => {
    if (!id) return;

    setLoading(true);
    try {
      // Use larger page size for graph view to get more data points
      const currentPageSize = forGraph ? graphPageSize : pageSize;
      const params = {
        deviceId: id,
        startTs: startTs ? startTs.valueOf() : undefined,
        endTs: endTs ? endTs.valueOf() : undefined,
        page: forGraph ? 1 : page + 1,
        pageSize: currentPageSize,
      };

      const response = await fetchCCTVHistory(params);
      const transformed = transformHistoryData(response.content);
      setHistoryData(transformed);
      setTotalElements(response.totalElements);

      // Try to get device name from first record
      if (response.content.length > 0) {
        const firstItem = response.content[0];
        const name = firstItem?.data?.[0]?.values?.['NAME'] || firstItem?.deviceName || id;
        setDeviceName(name);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  }, [id, startTs, endTs, page, pageSize, graphPageSize]);

  // Load data on mount and when params change
  useEffect(() => {
    fetchHistory(viewMode === 'graph');
  }, [fetchHistory, viewMode]);

  // Refetch when view mode changes
  useEffect(() => {
    fetchHistory(viewMode === 'graph');
  }, [viewMode]);

  // Quick date range selection
  const handleQuickRange = (days: number) => {
    const range = calculateDateRange(days);
    setStartTs(dayjs(range.startTs));
    setEndTs(dayjs(range.endTs));
    setPage(0);
  };

  // Handle search button click
  const handleSearch = () => {
    setPage(0);
    fetchHistory(viewMode === 'graph');
  };

  // Format timestamp for display
  const formatTimestamp = (isoString: string): string => {
    try {
      return dayjs(isoString).format('DD/MM/YYYY HH:mm:ss');
    } catch {
      return isoString;
    }
  };

  // Calculate pagination info
  const totalPages = Math.ceil(totalElements / pageSize);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <div className="cctv-history-page">
        {/* Header */}
        <div className="history-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate('/cctv-dashboard')}>
              <FaArrowLeft /> Back
            </button>
            <div className="title-section">
              <FaVideo className="title-icon" />
              <h1>Camera History</h1>
              {deviceName && <span className="device-name">{deviceName}</span>}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="history-filters">
          <div className="date-pickers">
            <DateTimePicker
              label="From"
              value={startTs}
              onChange={(date) => setStartTs(date)}
              renderInput={(params) => <TextField size="small" {...params} />}
            />
            <DateTimePicker
              label="To"
              value={endTs}
              onChange={(date) => setEndTs(date)}
              renderInput={(params) => <TextField size="small" {...params} />}
            />
            <Button variant="contained" onClick={handleSearch} disabled={loading}>
              Search
            </Button>
          </div>

          <div className="quick-ranges">
            <span className="label">Quick Range:</span>
            <ButtonGroup size="small" variant="outlined">
              {datePresets.map((preset) => (
                <Button key={preset.days} onClick={() => handleQuickRange(preset.days)}>
                  {preset.label}
                </Button>
              ))}
            </ButtonGroup>
          </div>

          <div className="view-toggle">
            <ButtonGroup size="small">
              <Button
                variant={viewMode === 'table' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('table')}
              >
                <FaTable /> Table
              </Button>
              <Button
                variant={viewMode === 'graph' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('graph')}
              >
                <FaChartLine /> Graph
              </Button>
            </ButtonGroup>
          </div>
        </div>

        {/* Content */}
        <div className="history-content">
          {loading ? (
            <div className="loading-container">
              <CircularProgress />
              <p>Loading history...</p>
            </div>
          ) : viewMode === 'table' ? (
            <div className="history-table-container">
              {historyData.length === 0 ? (
                <div className="no-data">
                  <p>No history data found for the selected date range.</p>
                </div>
              ) : (
                <>
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Status</th>
                        <th>Health</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.map((record) => (
                        <tr key={record.id}>
                          <td>{formatTimestamp(record.timestamp)}</td>
                          <td>
                            <span className={`status-badge ${record.status}`}>
                              {record.status === 'active' ? 'Working' : 'Non-Working'}
                            </span>
                          </td>
                          <td>{record.health}</td>
                          <td className="message-cell">{record.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="pagination">
                    <span className="pagination-info">
                      Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalElements)} of {totalElements}
                    </span>
                    <div className="pagination-controls">
                      <Button
                        size="small"
                        disabled={page === 0}
                        onClick={() => setPage(page - 1)}
                      >
                        Previous
                      </Button>
                      <span className="page-number">Page {page + 1} of {totalPages}</span>
                      <Button
                        size="small"
                        disabled={page >= totalPages - 1}
                        onClick={() => setPage(page + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="history-graph-container">
              {historyData.length === 0 ? (
                <div className="no-data">
                  <p>No history data found for the selected date range.</p>
                </div>
              ) : (
                <CCTVHistoryGraph data={historyData} deviceName={deviceName} />
              )}
            </div>
          )}
        </div>
      </div>
    </LocalizationProvider>
  );
};

export default CCTVCameraHistory;
